/** 中神迹:D9 极光 / D10 雷暴 / D11 昼夜逆转 / D12 生命潮汐 / D13 巨鲸掠空。 */
import { Graphics, Sprite } from 'pixi.js';
import { BaseEffect, EffectGroup } from './base';
import { weatherPool, eventPool } from '../engine/ParticlePool';
import { sky } from '../engine/SkyRenderer';
import { world as wind } from '../engine/Ticker';
import { scene } from '../engine/Scene';
import { TEX } from '../engine/Tex';
import { P } from '../engine/Palette';
import { easing } from '../engine/Easing';
import { LW, LH } from '../engine/Stage';
import { rand } from '../engine/util';
import { sfx } from '../engine/Sfx';

/** D9 极光(仅夜)
 *
 * 重写。旧版是 3 个 TEX.glow 拉成 332×1075 的竖向弥散椭圆左右摇——弥散圆
 * 没有"帘"的语言,读出来是三块发光雾斑,和真极光毫无相似点。真极光的视觉
 * 特征是:大量近平行的细竖光线,沿一条起伏的横向波带排布,底缘较亮、向上
 * 渐隐,整帘缓慢波动。TEX.streak(两端渐隐的细竖线)天然就是这个语言,拉
 * 高即得单根"光帘丝";28 根沿波带排开、青紫相间、各自呼吸,即成幕布。 */
export class AuroraEffect extends BaseEffect {
  id = 'aurora';
  // v3:横向波状光带(用户实测:竖丝帘不对,"极光是横着带状或者雾状")。
  // 三条丝带横贯天空:每条 = 上下两条相位错开的正弦曲线围成的闭合带,
  // 逐帧重绘,整带缓慢波动、厚度呼吸;青:紫:青,叠加 add 辉光,底衬一层横雾。
  private bands: { g: Graphics; color: number; baseY: number; ph: number; amp: number; th: number }[] = [];
  private under!: Sprite;
  constructor() { super(); this.layerName = 'auroraLayer'; }
  onStart() {
    scene.stage.layers.auroraLayer.visible = true;
    scene.crowd?.broadcast('idle', this.dur);
    this.under = new Sprite(TEX.glow());
    this.under.anchor.set(0.5); this.under.blendMode = 'add'; this.under.tint = P.aurora1;
    this.under.scale.set(5.2, 1.1); this.under.alpha = 0;
    this.under.position.set(LW * 0.5, LH * 0.2);
    this.g.addChild(this.under);
    const spec: [number, number][] = [[P.aurora1, 0.14], [P.aurora2, 0.2], [P.aurora1, 0.26]];
    for (const [color, fy] of spec) {
      const g = new Graphics();
      g.blendMode = 'add';
      this.g.addChild(g);
      this.bands.push({ g, color, baseY: LH * fy, ph: rand(0, 6.28), amp: rand(14, 26), th: rand(22, 40) });
    }
  }
  onUpdate() {
    const env = this.env(3);
    this.under.alpha = 0.09 * env;
    const N = 26;
    for (const b of this.bands) {
      const g = b.g; g.clear();
      const pts: [number, number][] = [];
      for (let i = 0; i <= N; i++) {
        const x = -40 + ((LW + 80) * i) / N;
        const y = b.baseY
          + Math.sin((i / N) * 4.2 + this.t * 0.4 + b.ph) * b.amp
          + Math.sin((i / N) * 9.1 - this.t * 0.23 + b.ph * 2) * b.amp * 0.4;
        pts.push([x, y]);
      }
      const th = b.th * (0.8 + 0.25 * Math.sin(this.t * 0.5 + b.ph));
      g.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i <= N; i++) g.lineTo(pts[i][0], pts[i][1]);
      for (let i = N; i >= 0; i--) {
        const w = th * (0.7 + 0.5 * Math.sin((i / N) * 6.3 + this.t * 0.6 + b.ph));
        g.lineTo(pts[i][0], pts[i][1] + w);
      }
      g.closePath();
      g.fill({ color: b.color, alpha: env * (0.16 + 0.06 * Math.sin(this.t * 0.8 + b.ph)) });
    }
  }
  onDestroy() { if (scene.stage.layers.auroraLayer.children.length <= 1) scene.stage.layers.auroraLayer.visible = false; }
}

/** D10 雷暴 */
export class ThunderstormEffect extends BaseEffect {
  id = 'thunderstorm';
  exclusiveGroup: EffectGroup = 'weather';
  private acc = 0; private bolt = new Graphics(); private boltT = 0; private next = 4; private popped = false;
  constructor() { super(); this.layerName = 'weatherFX'; }
  onStart() { sky.pushWeather('storm', 4); wind.setWind(-160); this.g.addChild(this.bolt); this.bolt.blendMode = 'add'; sfx.loop('storm', { fadeIn: 2.5 }); }
  onUpdate(dt: number) {
    const density = (200 + 200 * this.k) * this.env(3);
    this.acc += density * dt;
    while (this.acc >= 1) { this.acc -= 1; weatherPool?.spawn({ x: rand(0, LW), y: -30, vx: -160, vy: 900, life: 2, scaleX: 0.3, scaleY: 0.26, tint: P.rain, alpha: 0.55, texture: TEX.streak(), fade: 'none', onUpdate: (p) => { if (p.y > scene.island.surfaceYAt(p.x)) p.life = 0; } }); }
    this.next -= dt;
    if (this.next <= 0 && this.t < this.dur) { this.next = rand(4, 9); this.strike(); }
    if (this.boltT > 0) { this.boltT -= dt; if (this.boltT <= 0) this.bolt.clear(); }
    if (this.t > this.dur && !this.popped) { this.popped = true; sky.popWeather('storm', 6); wind.setWind(0); sfx.stop('storm', 5); }
  }
  /** 闪电:三层描边(宽蓝晕→中层→白芯)+ 1~2 条分叉 + 落点火花。
   *  旧版是一根 2px 单色细线闪 0.12s——在 1120px 的画面上没有存在感,
   *  "雷暴"的压迫感全靠 flash 硬闪,主体反而缺席。闪电的视觉分量来自
   *  辉光宽度(bloom 会再放大)和分叉的形态复杂度,不是线本身的亮度。
   *  注意 pixi v8 的 stroke() 消耗当前 path,叠三层必须每层重走一遍点。 */
  private strike() {
    this.stage.flash.flash(0.85, 0.1);
    this.stage.shake.shake(2, 0.4);
    sfx.play('thunder');
    const b = this.bolt;
    b.clear();
    const pts: number[][] = [];
    let x = rand(LW * 0.3, LW * 0.7), y = 0;
    pts.push([x, y]);
    while (y < LH * 0.62) { x += rand(-42, 42); y += rand(36, 64); pts.push([x, y]); }
    const draw = (path: number[][], wMul: number) => {
      const trace = () => { b.moveTo(path[0][0], path[0][1]); for (let i = 1; i < path.length; i++) b.lineTo(path[i][0], path[i][1]); };
      trace(); b.stroke({ width: 11 * wMul, color: 0x8fb0ff, alpha: 0.28 });
      trace(); b.stroke({ width: 4.5 * wMul, color: 0xdce6ff, alpha: 0.65 });
      trace(); b.stroke({ width: 1.8 * wMul, color: 0xffffff, alpha: 1 });
    };
    draw(pts, 1);
    const nb = 1 + (Math.random() < 0.5 ? 1 : 0);
    for (let k = 0; k < nb; k++) {
      const i0 = Math.floor(rand(1, pts.length - 2));
      const dir = Math.random() < 0.5 ? -1 : 1;
      let [bx, by] = pts[i0];
      const bp: number[][] = [[bx, by]];
      for (let s = 0; s < 3; s++) { bx += dir * rand(24, 48); by += rand(20, 40); bp.push([bx, by]); }
      draw(bp, 0.45);
    }
    const [ex, ey] = pts[pts.length - 1];
    for (let i = 0; i < 10; i++) eventPool?.spawn({ x: ex, y: ey, vx: rand(-70, 70), vy: rand(-90, -20), life: 0.5, scale: 0.03, tint: 0xdce6ff, alpha: 0.9, blend: 'add', texture: TEX.core(), fade: 'quad' });
    this.boltT = 0.16;
  }
  onResolve() { if (!this.popped) { this.popped = true; sky.popWeather('storm', 5); wind.setWind(0); sfx.stop('storm', 4); } }
  onDestroy() { if (sky.weather === 'storm') sky.popWeather('storm', 4); wind.setWind(0); sfx.stop('storm', 3); }
}

/** D11 昼夜逆转 */
export class DayNightFlipEffect extends BaseEffect {
  id = 'day_night_flip';
  exclusiveGroup: EffectGroup = 'sky';
  private cleared = false;
  onStart() { this.dur = 12; this.tail = 8; sky.overrideTo(sky.opposite(), 8); }
  onUpdate() { if (this.t > this.dur && !this.cleared) { this.cleared = true; sky.clearOverride(8); } }
  onResolve() { if (!this.cleared) { this.cleared = true; sky.clearOverride(6); } }
  onDestroy() { sky.clearOverride(4); }
}

/** D12 生命潮汐 */
export class LifeTideEffect extends BaseEffect {
  id = 'life_tide';
  private pillars: { s: Sprite; x: number; fall: number; landed: boolean }[] = [];
  // 语义强化(用户反馈"不知道在表达什么"):光柱落地 → 春绿光环自落点扩散 →
  // 新烛民自光中走出。"生命自天而降"三段式:降下(柱)、触地(环)、诞生(人)。
  private rings: { x: number; y: number; age: number }[] = [];
  private ringG = new Graphics();
  onStart() {
    this.dur = 12;
    this.ringG.blendMode = 'add';
    this.g.addChild(this.ringG);
    const n = 3 + Math.floor(4 * this.k);
    for (let i = 0; i < n; i++) {
      const x = rand(LW * 0.32, LW * 0.68);
      // 柱宽 0.4→0.22、α 0.5→0.38:102px 宽的 add 光柱经 bloom 一烧就是
      // 一根糊满画面中央的白色巨柱(用户截图实锤),光柱要"圣"不要"核爆"。
      const s = new Sprite(TEX.glow()); s.anchor.set(0.5, 0); s.blendMode = 'add'; s.tint = P.divine;
      s.scale.set(0.22, 0.01); s.position.set(x, 0); this.g.addChild(s);
      this.pillars.push({ s, x, fall: 0, landed: false });
    }
    scene.crowd?.spawnFromSky(n);
    scene.crowd?.gather(LW * 0.5, this.dur);
  }
  onUpdate(dt: number) {
    for (const p of this.pillars) {
      const gy = scene.island.surfaceYAt(p.x);
      if (!p.landed) {
        p.fall = Math.min(1, p.fall + dt / 1.5);
        p.s.scale.y = (gy / 256) * easing.quadOut(p.fall);
        if (p.fall >= 1) { p.landed = true; this.burst(p.x, gy); this.rings.push({ x: p.x, y: gy, age: 0 }); }
      }
      p.s.alpha = this.env(1) * 0.38 * (p.landed ? Math.max(0, 1 - (this.t - this.dur * 0.4) / 3) : 1);
    }
    // 落地光环:两圈春绿+divine 同心扩散
    this.ringG.clear();
    for (let i = this.rings.length - 1; i >= 0; i--) {
      const r = this.rings[i]; r.age += dt;
      const pr = r.age / 1.3;
      if (pr >= 1) { this.rings.splice(i, 1); continue; }
      this.ringG.ellipse(r.x, r.y, 26 + pr * 96, (26 + pr * 96) * 0.3).stroke({ width: 3, color: 0x9fe8a0, alpha: (1 - pr) * 0.6 });
      this.ringG.ellipse(r.x, r.y, 12 + pr * 60, (12 + pr * 60) * 0.3).stroke({ width: 2, color: P.divine, alpha: (1 - pr) * 0.5 });
    }
  }
  private burst(x: number, y: number) {
    for (let i = 0; i < 22; i++) { const a = (i / 22) * 6.28; eventPool?.spawn({ x, y, vx: Math.cos(a) * 80, vy: Math.sin(a) * 60 - 40, life: 1, scale: 0.03, tint: P.emberHi, alpha: 0.9, blend: 'add', texture: TEX.core(), fade: 'quad' }); }
  }
}

/** D13 巨鲸掠空 */
export class WhaleEffect extends BaseEffect {
  id = 'whale';
  private whale = new Graphics(); private dir = 1;
  // v4:巨大鲸鱼星座浮现于天顶(用户定稿:"一小只移动看着太奇怪",改为静止巨构)。
  // 不再横穿:星座固定在上层天空,放大 2.3 倍,星点在前 4.5 秒逐颗点亮(像有人
  // 在夜空连星),满座后整体呼吸,尾声整座淡去。
  private lit = 0;
  onStart() {
    this.dur = 14;
    this.whale.blendMode = 'add';
    this.whale.position.set(LW * 0.5, LH * 0.3);
    this.whale.scale.set(2.3);
    this.g.addChild(this.whale);
    scene.crowd?.broadcast('idle', this.dur);
  }
  onUpdate(dt: number) {
    void dt;
    this.whale.y = LH * 0.3 + Math.sin(this.t * 0.3) * 8;   // 仅极缓呼吸沉浮
    this.whale.alpha = this.env(2);
    this.lit = Math.min(1, this.t / 4.5);                    // 星点点亮进度
    // v3 星座鲸。实体剪影版(贝塞尔轮廓)被评"太丑达不到效果"——问题不在轮廓
    // 精度,在于**实体生物与本作"光的世界观"错频**:一块深色板子飘过天空,
    // 怎么修都是贴纸。改为星座画法:鲸由 18 颗星与星线勾出,只存在于夜空的
    // 想象里——它本身就是一场神迹,而非一只动物。形体极淡地衬底,尾鳍星点
    // 随摆动,眼是一粒燃着的 ember。
    const rim = sky.rimLight, g = this.whale;
    const flap = Math.sin(this.t * 1.8) * 10;
    const PTS: [number, number][] = [
      [-150, 6], [-104, -34], [-40, -48], [20, -40], [80, -26], [138, -6],   // 背线
      [206, -20 + flap], [204, 26 + flap], [140, 8],                          // 尾鳍两叉回尾柄
      [96, 20], [30, 36], [-46, 34], [-108, 26],                              // 腹线
    ];
    const litN = Math.floor(this.lit * PTS.length);
    g.clear();
    // 1) 形体衬底:满座后才隐约浮现
    if (this.lit >= 1) {
      g.moveTo(PTS[0][0], PTS[0][1]);
      for (let i = 1; i < PTS.length; i++) g.lineTo(PTS[i][0], PTS[i][1]);
      g.closePath().fill({ color: rim, alpha: 0.05 });
    }
    // 2) 星线:只连已点亮的星,像有人在夜空里逐笔连线
    if (litN > 1) {
      g.moveTo(PTS[0][0], PTS[0][1]);
      for (let i = 1; i < litN; i++) g.lineTo(PTS[i][0], PTS[i][1]);
      if (this.lit >= 1) g.closePath();
      g.stroke({ width: 1, color: P.moon, alpha: 0.32 });
    }
    // 3) 星点:逐颗点亮(新点亮的一颗闪白),各自呼吸
    for (let i = 0; i < litN; i++) {
      const fresh = i === litN - 1 && this.lit < 1 ? 1.8 : 1;
      const tw = 0.55 + 0.45 * Math.sin(this.t * 2.2 + i * 1.7);
      g.circle(PTS[i][0], PTS[i][1], (1.6 + tw * 1.6) * fresh).fill({ color: 0xeaf2ff, alpha: Math.min(1, (0.5 + 0.5 * tw) * fresh) });
    }
    if (this.lit >= 1) g.circle(-118, -8, 3.2).fill({ color: P.emberHi, alpha: 0.95 });   // 眼:满座后点睛
    if (Math.random() < 0.4) eventPool?.spawn({ x: this.whale.x + rand(-120, 120), y: this.whale.y + rand(-30, 30), vy: rand(-10, 10), life: 1.2, scale: 0.02, tint: P.moon, alpha: 0.5, blend: 'add', texture: TEX.glow() });
  }
}
